import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { apiPost } from '../../Utils';
import { Spinner } from 'reactstrap';

interface Props {
    bookingId: number;
    defaultLat?: number;
    defaultLng?: number;
}

const mapContainerStyle = {
    height: '100%',
    width: '100%'
};

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// Haversine distance formula in meters
const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const p = 0.017453292519943295;
    const c = Math.cos;
    const a = 0.5 - c((lat2 - lat1) * p) / 2 + c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) / 2;
    return 12742000 * Math.asin(Math.sqrt(a));
};

const getDirectionsPath = async (path: { lat: number, lng: number }[]) => {
    if (path.length < 2) return path;

    // Filter noise: Only keep points that are > 20 meters apart to reduce API payload
    const filteredPath = [path[0]];
    for (let i = 1; i < path.length; i++) {
        const lastP = filteredPath[filteredPath.length - 1];
        if (getDistance(lastP.lat, lastP.lng, path[i].lat, path[i].lng) > 20 || i === path.length - 1) {
            filteredPath.push(path[i]);
        }
    }

    if (filteredPath.length < 2) return filteredPath;

    let completeRoutePath: { lat: number, lng: number }[] = [];
    const chunkSize = 100; // Roads API supports up to 100 points per request

    for (let i = 0; i < filteredPath.length; i += chunkSize) {
        const chunk = filteredPath.slice(i, i + chunkSize);
        const pathString = chunk.map(p => `${p.lat},${p.lng}`).join('|');

        try {
            const response = await fetch(`https://roads.googleapis.com/v1/snapToRoads?path=${pathString}&interpolate=true&key=${apiKey}`);
            const data = await response.json();

            if (data.snappedPoints) {
                const snappedChunk = data.snappedPoints.map((sp: any) => ({
                    lat: sp.location.latitude,
                    lng: sp.location.longitude
                }));
                completeRoutePath = [...completeRoutePath, ...snappedChunk];
            } else {
                console.warn("Roads API no snapped points returned. Using raw chunk.");
                completeRoutePath = [...completeRoutePath, ...chunk];
            }
        } catch (error) {
            console.error("Error with Roads API:", error);
            completeRoutePath = [...completeRoutePath, ...chunk];
        }
    }
    return completeRoutePath;
};



const decodePolyline = (encoded: string) => {
    let points = [];
    let index = 0, len = encoded.length;
    let lat = 0, lng = 0;
    while (index < len) {
        let b, shift = 0, result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;
        shift = 0;
        result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;
        points.push({ lat: (lat / 1E5), lng: (lng / 1E5) });
    }
    return points;
};

export default function LocationHistoryMap({ bookingId, defaultLat, defaultLng }: Props) {
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: apiKey,
    });

    const [loading, setLoading] = useState(true);
    const [patientPath, setPatientPath] = useState<{ lat: number, lng: number }[]>([]);
    const [bystanderPath, setBystanderPath] = useState<{ lat: number, lng: number }[]>([]);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const response: any = await apiPost('booking/location-history', { booking_id: bookingId });
                if (response.status) {
                    console.log(`[LocationHistoryMap] Raw data for bookingId ${bookingId}:`, response.data);
                    const pPath = response.data.patient_path.map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
                    const bPath = response.data.bystander_path.map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) }));

                    const snappedPatientPath = await getDirectionsPath(pPath);
                    const snappedBystanderPath = await getDirectionsPath(bPath);

                    setPatientPath(snappedPatientPath);
                    setBystanderPath(snappedBystanderPath);
                }
            } catch (error) {
                console.error("Error fetching location history:", error);
            } finally {
                setLoading(false);
            }
        };

        if (bookingId) {
            fetchHistory();
        } else {
            setLoading(false);
        }
    }, [bookingId]);

    // Fit map bounds whenever paths or map instance change
    useEffect(() => {
        if (mapInstance && isLoaded) {
            const allPoints = [...patientPath, ...bystanderPath];
            if (allPoints.length > 0) {
                const bounds = new google.maps.LatLngBounds();
                allPoints.forEach(point => bounds.extend(point));

                // Always include the booking service location in the view bounds
                if (defaultLat && defaultLng) {
                    bounds.extend({ lat: defaultLat, lng: defaultLng });
                }

                mapInstance.fitBounds(bounds);

                // Add some padding to bounds
                const listener = google.maps.event.addListener(mapInstance, "idle", () => {
                    if (mapInstance.getZoom()! > 16) mapInstance.setZoom(16);
                    google.maps.event.removeListener(listener);
                });
            } else if (defaultLat && defaultLng) {
                mapInstance.panTo({ lat: defaultLat, lng: defaultLng });
                mapInstance.setZoom(15);
            }
        }
    }, [patientPath, bystanderPath, mapInstance, isLoaded, defaultLat, defaultLng]);

    const onLoad = useCallback(function callback(map: google.maps.Map) {
        setMapInstance(map);
    }, []);

    const onUnmount = useCallback(function callback(map: google.maps.Map) {
        setMapInstance(null);
    }, []);

    if (loading || !isLoaded) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}><Spinner color="primary" /></div>;
    }

    const center = defaultLat && defaultLng ? { lat: defaultLat, lng: defaultLng } : { lat: 20.5937, lng: 78.9629 };

    // Symbol for directional arrows
    const arrowSymbol = window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 2;

    return (
        <div style={{ height: '300px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', position: 'relative' }}>
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={13}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                }}
            >
                {patientPath.length > 0 && (
                    <Polyline
                        path={patientPath}
                        options={{
                            strokeColor: "#ef4444",
                            strokeWeight: 8,
                            strokeOpacity: 0.4,
                            zIndex: 1,
                            icons: [{
                                icon: { path: arrowSymbol, scale: 2.5, strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 2 },
                                offset: '100%',
                                repeat: '100px'
                            }]
                        }}
                    />
                )}

                {bystanderPath.length > 0 && (
                    <Polyline
                        path={bystanderPath}
                        options={{
                            strokeColor: "#3b82f6",
                            strokeWeight: 4,
                            strokeOpacity: 0.9,
                            zIndex: 2,
                            icons: [{
                                icon: { path: arrowSymbol, scale: 2.5, strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 2 },
                                offset: '100%',
                                repeat: '100px'
                            }]
                        }}
                    />
                )}

                {patientPath.length > 0 && (
                    <Marker
                        position={patientPath[patientPath.length - 1]}
                        title="Patient Current Location"
                        icon="http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                    />
                )}

                {bystanderPath.length > 0 && (
                    <Marker
                        position={bystanderPath[bystanderPath.length - 1]}
                        title="Bystander Current Location"
                        icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    />
                )}
            </GoogleMap>

            {/* Legend */}
            <div style={{
                position: 'absolute',
                bottom: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '12px 20px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                border: '1px solid #f3f4f6',
                zIndex: 10
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '6px', backgroundColor: '#ef4444', opacity: 0.6, borderRadius: '3px' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>Patient Path</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>Bystander Path</span>
                </div>
            </div>
        </div>
    );
}











// import React, { useEffect, useState, useCallback } from 'react';
// import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
// import { apiPost } from '../../Utils';
// import { Spinner } from 'reactstrap';

// interface Props {
//     bookingId: number;
//     defaultLat?: number;
//     defaultLng?: number;
// }

// const mapContainerStyle = {
//     height: '100%',
//     width: '100%'
// };

// const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// // Haversine distance formula in meters
// const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
//     const p = 0.017453292519943295;
//     const c = Math.cos;
//     const a = 0.5 - c((lat2 - lat1) * p) / 2 + c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) / 2;
//     return 12742000 * Math.asin(Math.sqrt(a));
// };

// const getDirectionsPath = async (path: { lat: number, lng: number }[]) => {
//     if (path.length < 2) return path;

//     // Filter noise: Only keep points that are > 50 meters apart to fix GPS jitter loops
//     const filteredPath = [path[0]];
//     for (let i = 1; i < path.length; i++) {
//         const lastP = filteredPath[filteredPath.length - 1];
//         if (getDistance(lastP.lat, lastP.lng, path[i].lat, path[i].lng) > 50 || i === path.length - 1) {
//             filteredPath.push(path[i]);
//         }
//     }

//     if (filteredPath.length < 2) return filteredPath;

//     let completeRoutePath: { lat: number, lng: number }[] = [];

//     // We can use up to 25 waypoints (27 points total including origin/dest) per request
//     const chunkSize = 26; // chunking by 26 means each segment overlaps by 1 point

//     for (let i = 0; i < filteredPath.length - 1; i += chunkSize) {
//         const chunk = filteredPath.slice(i, i + chunkSize + 1);
//         if (chunk.length < 2) break;

//         const origin = chunk[0];
//         const destination = chunk[chunk.length - 1];
//         let intermediates: any[] = [];

//         if (chunk.length > 2) {
//             intermediates = chunk.slice(1, chunk.length - 1).map(p => ({
//                 location: { latLng: { latitude: p.lat, longitude: p.lng } }
//             }));
//         }

//         const payload: any = {
//             origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
//             destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
//             travelMode: "DRIVE",
//         };

//         if (intermediates.length > 0) {
//             payload.intermediates = intermediates;
//         }

//         try {
//             const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'X-Goog-Api-Key': apiKey,
//                     'X-Goog-FieldMask': 'routes.polyline.encodedPolyline'
//                 },
//                 body: JSON.stringify(payload)
//             });

//             const data = await response.json();

//             if (data.error) {
//                 console.error("Routes API Error:", data.error.message);
//                 completeRoutePath = [...completeRoutePath, ...chunk];
//             } else if (data.routes && data.routes.length > 0 && data.routes[0].polyline) {
//                 const encodedPath = data.routes[0].polyline.encodedPolyline;
//                 const decodedChunk = decodePolyline(encodedPath);
//                 completeRoutePath = [...completeRoutePath, ...decodedChunk];
//             } else {
//                 completeRoutePath = [...completeRoutePath, ...chunk];
//             }
//         } catch (error) {
//             console.error("Error with Routes API Request:", error);
//             completeRoutePath = [...completeRoutePath, ...chunk];
//         }
//     }

//     return completeRoutePath;
// };

// const decodePolyline = (encoded: string) => {
//     let points = [];
//     let index = 0, len = encoded.length;
//     let lat = 0, lng = 0;
//     while (index < len) {
//         let b, shift = 0, result = 0;
//         do {
//             b = encoded.charCodeAt(index++) - 63;
//             result |= (b & 0x1f) << shift;
//             shift += 5;
//         } while (b >= 0x20);
//         let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
//         lat += dlat;
//         shift = 0;
//         result = 0;
//         do {
//             b = encoded.charCodeAt(index++) - 63;
//             result |= (b & 0x1f) << shift;
//             shift += 5;
//         } while (b >= 0x20);
//         let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
//         lng += dlng;
//         points.push({ lat: (lat / 1E5), lng: (lng / 1E5) });
//     }
//     return points;
// };

// export default function LocationHistoryMap({ bookingId, defaultLat, defaultLng }: Props) {
//     const { isLoaded } = useJsApiLoader({
//         id: "google-map-script",
//         googleMapsApiKey: apiKey,
//     });

//     const [loading, setLoading] = useState(true);
//     const [patientPath, setPatientPath] = useState<{ lat: number, lng: number }[]>([]);
//     const [bystanderPath, setBystanderPath] = useState<{ lat: number, lng: number }[]>([]);
//     const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

//     useEffect(() => {
//         const fetchHistory = async () => {
//             try {
//                 const response: any = await apiPost('booking/location-history', { booking_id: bookingId });
//                 if (response.status) {
//                     const pPath = response.data.patient_path.map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
//                     const bPath = response.data.bystander_path.map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) }));

//                     const snappedPatientPath = await getDirectionsPath(pPath);
//                     const snappedBystanderPath = await getDirectionsPath(bPath);

//                     setPatientPath(snappedPatientPath);
//                     setBystanderPath(snappedBystanderPath);
//                 }
//             } catch (error) {
//                 console.error("Error fetching location history:", error);
//             } finally {
//                 setLoading(false);
//             }
//         };

//         if (bookingId) {
//             fetchHistory();
//         } else {
//             setLoading(false);
//         }
//     }, [bookingId]);

//     // Fit map bounds whenever paths or map instance change
//     useEffect(() => {
//         if (mapInstance && isLoaded) {
//             const allPoints = [...patientPath, ...bystanderPath];
//             if (allPoints.length > 0) {
//                 const bounds = new google.maps.LatLngBounds();
//                 allPoints.forEach(point => bounds.extend(point));

//                 // Always include the booking service location in the view bounds
//                 if (defaultLat && defaultLng) {
//                     bounds.extend({ lat: defaultLat, lng: defaultLng });
//                 }

//                 mapInstance.fitBounds(bounds);

//                 // Add some padding to bounds
//                 const listener = google.maps.event.addListener(mapInstance, "idle", () => {
//                     if (mapInstance.getZoom()! > 16) mapInstance.setZoom(16);
//                     google.maps.event.removeListener(listener);
//                 });
//             } else if (defaultLat && defaultLng) {
//                 mapInstance.panTo({ lat: defaultLat, lng: defaultLng });
//                 mapInstance.setZoom(15);
//             }
//         }
//     }, [patientPath, bystanderPath, mapInstance, isLoaded, defaultLat, defaultLng]);

//     const onLoad = useCallback(function callback(map: google.maps.Map) {
//         setMapInstance(map);
//     }, []);

//     const onUnmount = useCallback(function callback(map: google.maps.Map) {
//         setMapInstance(null);
//     }, []);

//     if (loading || !isLoaded) {
//         return <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}><Spinner color="primary" /></div>;
//     }

//     const center = defaultLat && defaultLng ? { lat: defaultLat, lng: defaultLng } : { lat: 20.5937, lng: 78.9629 };

//     // Symbol for directional arrows
//     const arrowSymbol = window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 2;

//     return (
//         <div style={{ height: '300px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', position: 'relative' }}>
//             <GoogleMap
//                 mapContainerStyle={mapContainerStyle}
//                 center={center}
//                 zoom={13}
//                 onLoad={onLoad}
//                 onUnmount={onUnmount}
//                 options={{
//                     streetViewControl: false,
//                     mapTypeControl: false,
//                 }}
//             >
//                 {patientPath.length > 0 && (
//                     <Polyline
//                         path={patientPath}
//                         options={{
//                             strokeColor: "#ef4444",
//                             strokeWeight: 4,
//                             strokeOpacity: 0.8,
//                             icons: [{
//                                 icon: { path: arrowSymbol, scale: 2.5, strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 2 },
//                                 offset: '100%',
//                                 repeat: '100px'
//                             }]
//                         }}
//                     />
//                 )}

//                 {bystanderPath.length > 0 && (
//                     <Polyline
//                         path={bystanderPath}
//                         options={{
//                             strokeColor: "#3b82f6",
//                             strokeWeight: 4,
//                             strokeOpacity: 0.8,
//                             icons: [{
//                                 icon: { path: arrowSymbol, scale: 2.5, strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 2 },
//                                 offset: '100%',
//                                 repeat: '100px'
//                             }]
//                         }}
//                     />
//                 )}

//                 {patientPath.length > 0 && (
//                     <Marker
//                         position={patientPath[patientPath.length - 1]}
//                         title="Patient Current Location"
//                         icon="http://maps.google.com/mapfiles/ms/icons/red-dot.png"
//                     />
//                 )}

//                 {bystanderPath.length > 0 && (
//                     <Marker
//                         position={bystanderPath[bystanderPath.length - 1]}
//                         title="Bystander Current Location"
//                         icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
//                     />
//                 )}
//             </GoogleMap>

//             {/* Legend */}
//             <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-6 border border-gray-100">
//                 <div className="flex items-center gap-2">
//                     <div className="w-6 h-1 bg-[#ef4444] rounded-full"></div>
//                     <span className="text-sm font-semibold text-gray-700">Patient Path</span>
//                 </div>
//                 <div className="flex items-center gap-2">
//                     <div className="w-6 h-1 bg-[#3b82f6] rounded-full"></div>
//                     <span className="text-sm font-semibold text-gray-700">Bystander Path</span>
//                 </div>
//             </div>
//         </div>
//     );
// }
